// STEP1-OUTBOX (build order increment 1 B; integrations design §2.5, §2.6): the outbox of outward
// acts (pending, sent, failed, dead letters; retry and cancel) and the grants of a connection.
//
//   GET  outbox                              managers: every item; others: the items they asked
//                                            for (themselves or their crew) or decided
//   POST outbox/{id}/retry                   a dead letter or a failed item, attempted again now
//   POST outbox/{id}/cancel                  a queued, failed or dead item never goes out
//   GET  connections/{id}/grants             who may use a connection (its managers)
//   POST connections/{id}/grants             grant (or regrant) a person, DE, seat, team or crew
//   POST connections/{id}/grants/{grant_id}/revoke
//   GET  connection-grants                   INC2-CONTACT: every live grant the caller manages
import { z } from "zod";
import {
  ConnectionFamily,
  ConnectionGranteeKind,
  ConnectionGrantLevel,
  OutboxStatus,
} from "../enums";
import { ActionOutboxItem, ConnectionGrant, ConnectionGrantLimits } from "../outbox-rows";
import { LIMITS, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const agents = { feature: "agents", tag: "outbox" } as const;
const core = { feature: "core", tag: "connections" } as const;

/** The list's groups (the Outbox tabs): pending, sent, failed (retrying and dead letters). */
export const OUTBOX_GROUPS = {
  pending: ["queued", "sending"],
  sent: ["sent"],
  failed: ["failed", "dead"],
} as const;
export const OutboxListQuery = PageQuery.extend({
  status: OutboxStatus.optional(),
  group: z.enum(["pending", "sent", "failed"]).optional(),
  /** One item by id (an inbox notice's link), whatever its status; still the caller's to see. */
  item: Uuid.optional(),
  /** INC2-CONTACT (Connections drawer): the items through one connection. */
  connection: Uuid.optional(),
  /** INC2-CONTACT (a DE's Connections tab): the items one member (a DE) asked for. */
  requested_by: Uuid.optional(),
  /** CORE-RECEIPTS (an approval's page): the act one approval put in the outbox. */
  approval: Uuid.optional(),
});

/**
 * INC2-CONTACT (Connections UI, integrations §2.10): the live grants on every connection the
 * caller manages, optionally those of one connection or those one member holds (directly, through
 * a seat it holds, or a team of those seats).
 */
export const ConnectionGrantsQuery = z.object({
  connection: Uuid.optional(),
  holder: Uuid.optional(),
});
/**
 * INC2-CONTACT: what a grant manager may see of a connection it manages (never secrets), so a
 * delegated manager, who cannot list company connections, can still name it.
 */
export const ManagedConnection = z
  .object({
    id: Uuid,
    label: z.string(),
    provider: z.string(),
    /** The account it is signed in as (`ana@client.test`), when known. */
    identity_display: z.string().nullable(),
    state: z.string(),
  })
  .meta({ id: "ManagedConnection" });
export type ManagedConnection = z.infer<typeof ManagedConnection>;
export const OutboxCancel = z
  .strictObject({ reason: z.string().max(LIMITS.text).optional() })
  .meta({ id: "OutboxCancel" });

export const ConnectionGrantCreate = z
  .strictObject({
    grantee_kind: ConnectionGranteeKind,
    grantee_id: Uuid,
    level: ConnectionGrantLevel,
    /** Empty or absent: every family the connection serves. */
    families: z.array(ConnectionFamily).max(5).optional(),
    limits: ConnectionGrantLimits.optional(),
  })
  .meta({ id: "ConnectionGrantCreate" });
export const ConnectionGrantParams = CompanyItemParams.extend({ grant_id: Uuid });

export const outboxRoutes = {
  "outbox.list": defineRoute({
    ...agents,
    id: "outbox.list",
    method: "get",
    path: `${co}/outbox`,
    summary: "Outward acts: queued, sent, failed and dead letters, newest first",
    access: "member",
    params: CompanyParams,
    query: OutboxListQuery,
    response: { status: 200, schema: page(ActionOutboxItem) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "outbox.retry": defineRoute({
    ...agents,
    id: "outbox.retry",
    method: "post",
    path: `${co}/outbox/{id}/retry`,
    summary: "Attempt a dead letter or a failed outward act again now",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: ActionOutboxItem },
    errors: ["outbox_state_conflict"],
    emits: ["outbox.retried"],
  }),
  "outbox.cancel": defineRoute({
    ...agents,
    id: "outbox.cancel",
    method: "post",
    path: `${co}/outbox/{id}/cancel`,
    summary: "Cancel an outward act that has not gone out",
    access: "member",
    params: CompanyItemParams,
    body: OutboxCancel,
    idempotency: "optional",
    response: { status: 200, schema: ActionOutboxItem },
    errors: ["outbox_state_conflict"],
    emits: ["outbox.cancelled", "approval.executed", "wakeup.requested"],
  }),
  "connectionGrants.list": defineRoute({
    ...core,
    id: "connectionGrants.list",
    method: "get",
    path: `${co}/connections/{id}/grants`,
    summary: "Who may use a connection: people, DEs, seats, teams and crews, with level and limits",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(ConnectionGrant) }) },
    errors: [],
    emits: [],
  }),
  "connectionGrants.listAll": defineRoute({
    ...core,
    id: "connectionGrants.listAll",
    method: "get",
    path: `${co}/connection-grants`,
    summary:
      "Live grants on the connections the caller manages (for one connection, or held by one member)",
    access: "member",
    params: CompanyParams,
    query: ConnectionGrantsQuery,
    response: {
      status: 200,
      schema: z.object({
        data: z.array(ConnectionGrant),
        /** The connections the grants are on (each one the caller manages). */
        connections: z.array(ManagedConnection),
      }),
    },
    errors: [],
    emits: [],
  }),
  "connectionGrants.create": defineRoute({
    ...core,
    id: "connectionGrants.create",
    method: "post",
    path: `${co}/connections/{id}/grants`,
    summary:
      "Grant a person, DE, seat, team or crew the use of a connection (replaces their grant)",
    access: "member",
    params: CompanyItemParams,
    body: ConnectionGrantCreate,
    idempotency: "optional",
    response: { status: 201, schema: ConnectionGrant },
    errors: ["grant_not_allowed"],
    emits: ["connection.grant_changed"],
  }),
  "connectionGrants.revoke": defineRoute({
    ...core,
    id: "connectionGrants.revoke",
    method: "post",
    path: `${co}/connections/{id}/grants/{grant_id}/revoke`,
    summary: "End a grant on a connection",
    access: "member",
    params: ConnectionGrantParams,
    idempotency: "optional",
    response: { status: 200, schema: ConnectionGrant },
    errors: ["grant_not_allowed"],
    emits: ["connection.grant_changed"],
  }),
} as const;
